import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { patchingApi } from '@/services/patching'
import { formatDateTime } from '@/utils/format'
import { isLocalRunUrl, PATCH_POLL_INTERVAL_MS, safeExternalUrl } from '@/utils/patchStage'
import type { CampaignReport as CampaignReportData } from '@/types/patching'
import styles from './Patching.module.css'

const RUNNING = new Set(['plan_dispatched', 'plan_running', 'execute_dispatched', 'executing', 'validating'])

function reportIsRunning(data: CampaignReportData | undefined): boolean {
  return (data?.stages ?? []).some((stage) => RUNNING.has(stage.status))
}

function visibleResults(stage: CampaignReportData['stages'][number]) {
  return (stage.results ?? []).filter((row) => {
    if (stage.dry_run) return true
    const text = `${row.error ?? ''} ${row.skipped_reason ?? ''}`.toLowerCase()
    return !(text.includes('dry run') || text.includes('was not called'))
  })
}

/** Campaign report from the backend. It contains results and limitations, never credentials. */
export function CampaignReport({ campaignId }: { campaignId: string }) {
  const report = useQuery({
    queryKey: ['patching', 'report', campaignId],
    queryFn: () => patchingApi.getReport(campaignId),
    refetchInterval: (query) => (reportIsRunning(query.state.data) ? PATCH_POLL_INTERVAL_MS : false),
  })

  if (report.isLoading) return <p className={styles.muted}>Loading the campaign report…</p>
  if (report.isError) {
    return (
      <div className={clsx(styles.alert, styles.alertError)} role="alert">
        {report.error.message}
      </div>
    )
  }
  const data = report.data
  if (!data) return null
  return (
    <div className={styles.stack} data-testid="campaign-report">
      <p>{data.note}</p>
      <dl className={styles.factGrid}>
        <div><dt className={styles.factLabel}>Campaign</dt><dd className={styles.factValue}>{data.campaign_id}</dd></div>
        <div><dt className={styles.factLabel}>Change</dt><dd className={styles.factValue}>{data.change_ticket || '—'}</dd></div>
        <div><dt className={styles.factLabel}>Status</dt><dd className={styles.factValue}>{data.status}</dd></div>
      </dl>
      {data.schedules.length > 0 && (
        <section>
          <h3 className={styles.sectionTitle}>Schedules</h3>
          <ul>
            {data.schedules.map((row) => (
              <li key={row.id}>
                {row.environment.toUpperCase()} {row.status}: {formatDateTime(row.start_at)} – {formatDateTime(row.end_at)}
              </li>
            ))}
          </ul>
        </section>
      )}
      {data.stages.map((stage) => (
        <section key={stage.environment}>
          <h3 className={styles.sectionTitle}>{stage.environment.toUpperCase()} — {stage.status}</h3>
          <RunLink stage={stage} />
          {stage.failure?.reason && <p className={styles.muted}>{stage.failure.reason}</p>}
          {(stage.steps ?? []).length > 0 && (
            <ul className={styles.checkList} data-testid="report-steps">
              {stage.steps?.map((step) => (
                <li key={step.name}>
                  <span className={styles.mono}>{step.status || 'recorded'}</span> {step.name}
                  {step.message ? ` — ${step.message}` : ''}
                </li>
              ))}
            </ul>
          )}
          {visibleResults(stage).length === 0 ? (
            <p className={styles.muted}>No execution results recorded.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>VM</th>
                    <th>Result</th>
                    <th>Compliance</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleResults(stage).map((row) => (
                    <tr key={row.vm_name}>
                      <td>{row.vm_name}</td>
                      <td>{row.status}</td>
                      <td>{row.compliance_state}</td>
                      <td>{row.error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
      <section>
        <h3 className={styles.sectionTitle}>Pre-patch checks</h3>
        {data.prechecks.length === 0 ? (
          <p className={styles.muted}>Pre-patch validation has not been run.</p>
        ) : (
          <ul>
            {data.prechecks.map((row) => (
              <li key={`${row.resource_name}-${row.check}-${row.detail}`}>
                {row.resource_name} {row.check}: {row.status}. {row.detail}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function RunLink({ stage }: { stage: CampaignReportData['stages'][number] }) {
  const external = isLocalRunUrl(stage.run_url) ? null : safeExternalUrl(stage.run_url)
  if (external) {
    return (
      <p>
        <a href={external} target="_blank" rel="noopener noreferrer" data-testid="report-pipeline">Open pipeline</a>
      </p>
    )
  }
  if (isLocalRunUrl(stage.run_url) || stage.run_id) {
    return (
      <p data-testid="report-local">
        This run is inside Platform Central. The dev site does not start a GitHub Actions workflow, so there is no Actions page to open.
        {stage.run_id ? ` Run ${stage.run_id}.` : ''}
      </p>
    )
  }
  return null
}
